import React, { FC, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { Link } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ProductCard from "components/ProductCard";
import { clearWishlist } from "../../redux/Wishlist/wishlistSlice";
import toast from "react-hot-toast";
import FadeInUpOnScroll from "components/Animation/FadeInUpOnScroll";
import { Transition } from "@headlessui/react";
import { ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/24/outline";

export interface PageWishlistWorkingProps {
  className?: string;
}

const PageWishlistWorking: FC<PageWishlistWorkingProps> = ({ className = "" }) => {
  const dispatch = useDispatch();
  const wishlistItems = useSelector((state: RootState) => state.wishlist.items);
  const [showClearModal, setShowClearModal] = useState(false);

  const handleClearAll = () => {
    if (wishlistItems.length === 0) return;
    setShowClearModal(true);
  };

  const confirmClearAll = () => {
    dispatch(clearWishlist());
    toast.success("Wishlist cleared!");
    setShowClearModal(false);
  };

  const cancelClearAll = () => {
    setShowClearModal(false);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showClearModal) {
        cancelClearAll();
      }
    };

    if (showClearModal) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showClearModal]);

  return (
    <div className={`nc-PageWishlistWorking ${className}`} data-nc-id="PageWishlistWorking">
      <Helmet>
        <title>My Wishlist | Meet Solution</title>
      </Helmet>

      <div className="container py-16 lg:pb-28 lg:pt-20">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            My Wishlist
          </h1>
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {wishlistItems.length > 0 
              ? `You have ${wishlistItems.length} item${wishlistItems.length > 1 ? 's' : ''} in your wishlist`
              : "Your wishlist is empty"
            }
          </p>
        </div>

        {/* Clear All Button */}
        {wishlistItems.length > 0 && (
          <div className="mb-8 flex justify-end">
            <button
              onClick={handleClearAll}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear All Items
            </button>
          </div>
        )}

        {wishlistItems.length > 0 ? (
          <div className={`grid grid-cols-12 gap-5`}>
            {wishlistItems.map((item: any, index: number) => (
              <div className="col-span-12 lg:col-span-3" key={index}>
                <FadeInUpOnScroll defaultClassName="animated" className="fadeInUp">
                  <ProductCard data={item} key={index} />
                </FadeInUpOnScroll>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="mb-8">
              <svg
                className="w-24 h-24 text-gray-400 dark:text-gray-600 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Your wishlist is empty
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Start adding products to your wishlist by clicking the heart icon on any product card.
            </p>
            <Link to="/our-product">
              <ButtonPrimary>
                Browse Products
              </ButtonPrimary>
            </Link>
          </div>
        )}
      </div>

      {/* Custom Clear All Confirmation Modal */}
      <Transition
        show={showClearModal}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={cancelClearAll}
            />

            {/* Modal */}
            <Transition
              show={showClearModal}
              enter="transition-all duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition-all duration-300"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                {/* Header */}
                <div className="flex items-center justify-center p-6 border-b border-gray-200 dark:border-neutral-700">
                  <div className="flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full">
                    <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 text-center">
                  <h3 id="modal-title" className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Clear Wishlist
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to remove all {wishlistItems.length} item{wishlistItems.length > 1 ? 's' : ''} from your wishlist?
                    <br />
                    <span className="text-red-600 dark:text-red-400 font-medium">This action cannot be undone.</span>
                  </p>

                  {/* Buttons */}
                  <div className="flex gap-3 justify-center">
                    <ButtonSecondary
                      onClick={cancelClearAll}
                      className="px-6 py-2 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700"
                    >
                      Cancel
                    </ButtonSecondary>
                    <ButtonPrimary
                      onClick={confirmClearAll}
                      className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Clear All
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default PageWishlistWorking;
